'use client';

import type { ReactNode } from 'react';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { Brain, Eye, EyeOff, Lightbulb } from 'lucide-react';
import { useState } from 'react';

import type { DifficultyLevel } from '../constants';
import type { VideoProps } from '../types/video';

import DifficultyBadge from './difficulty-badge';
import VideoSection from './video-section';

interface StepAnswer {
  content: ReactNode;
  title: ReactNode;
}

interface ThinkingExerciseProps extends VideoProps {
  answer: ReactNode | StepAnswer[] | { title: ReactNode; content: ReactNode };
  children: ReactNode;
  description?: ReactNode;
  difficulty: DifficultyLevel;
  problemNumber: number;
  title: string;
}

const ThinkingExercise = ({
  answer,
  children,
  description,
  difficulty,
  problemNumber,
  title,
  videoData,
  videoId,
}: ThinkingExerciseProps) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const { trackEvent } = useInteractionTracker();

  // Use consistent text handled in code
  const hideText = 'Hide Answer';
  const showText = 'Show Answer';

  const toggleAnswer = () => {
    const next = !showAnswer;
    setShowAnswer(next);
    void trackEvent({
      action: next ? 'show-answer' : 'hide-answer',
      category: 'exercise',
      label: `problem-${problemNumber}`,
    });
  };

  return (
    <div className="surface-card rounded-xl shadow-lg mb-8 overflow-hidden hover:shadow-xl transition-shadow">
      <div
        className={`h-2 ${
          difficulty === 'Beginner'
            ? 'difficulty-stripe-beginner'
            : difficulty === 'Intermediate'
              ? 'difficulty-stripe-intermediate'
              : 'difficulty-stripe-advanced'
        }`}
      />
      <div className="border-b border-glass p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div
              className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl text-white font-bold text-base sm:text-lg flex-shrink-0 ${
                difficulty === 'Beginner'
                  ? 'difficulty-circle-beginner'
                  : difficulty === 'Intermediate'
                    ? 'difficulty-circle-intermediate'
                    : 'difficulty-circle-advanced'
              }`}
            >
              {problemNumber}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-bold text-primary mb-1 truncate">
                {title}
              </h3>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-secondary">Thinking Exercise</span>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <DifficultyBadge difficulty={difficulty} />
          </div>
        </div>
      </div>

      <div className="p-6">
        {videoData && videoId && videoData.src && (
          <VideoSection videoData={videoData} videoId={videoId} />
        )}

        {description && (
          <div className="explanation-box mb-6 no-math">
            <div className="flex items-start gap-3">
              <Brain
                aria-hidden="true"
                className="w-5 h-5 text-secondary mt-1 flex-shrink-0"
              />
              <div>
                <h4 className="font-semibold text-primary mb-2">
                  Think About This
                </h4>
                <p className="text-secondary leading-relaxed">{description}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">{children}</div>

        <div className="flex items-center gap-4 mt-6">
          <button
            aria-controls="thinking-answer"
            aria-describedby="thinking-instructions"
            aria-expanded={showAnswer}
            aria-label={`${showAnswer ? hideText : showText} - ${showAnswer ? 'Hide' : 'Show'} the explanation for this thinking exercise`}
            className="cta-button-primary"
            onClick={toggleAnswer}
            type="button"
          >
            {showAnswer ? (
              <EyeOff aria-hidden="true" className="w-4 h-4" />
            ) : (
              <Eye aria-hidden="true" className="w-4 h-4" />
            )}
            {showAnswer ? hideText : showText}
          </button>
        </div>

        {/* Hidden instructions for screen readers */}
        <div className="sr-only" id="thinking-instructions">
          Click to {showAnswer ? 'hide' : 'reveal'} the detailed explanation for
          this thinking exercise.
        </div>

        {/* Screen reader announcement for answer reveal */}
        <div aria-live="polite" className="sr-only">
          {showAnswer && 'Answer explanation is now visible.'}
        </div>

        {showAnswer && (
          <div
            className="mt-6 solution-box animate-fade-in-scale-bounce"
            id="thinking-answer"
          >
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-exercise-thinking mt-1 flex-shrink-0" />
              <div className="flex-1">
                {Array.isArray(answer) &&
                (answer as unknown[]).every(
                  (s: unknown) =>
                    !!s &&
                    typeof s === 'object' &&
                    'title' in (s as Record<string, unknown>) &&
                    'content' in (s as Record<string, unknown>)
                ) ? (
                  <>
                    <h4 className="font-semibold text-primary mb-4">
                      Explanation
                    </h4>
                    <div className="space-y-4">
                      {(
                        answer as Array<{
                          title: ReactNode;
                          content: ReactNode;
                        }>
                      ).map((step, index) => (
                        <div
                          className="p-4 rounded-lg step-default"
                          key={index}
                        >
                          <h5 className="font-semibold text-primary mb-2">
                            {step.title}
                          </h5>
                          <div className="text-secondary leading-relaxed">
                            {step.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : answer &&
                  typeof answer === 'object' &&
                  'title' in answer &&
                  'content' in answer ? (
                  <>
                    <h4 className="font-semibold text-primary mb-4">
                      Explanation
                    </h4>
                    <div className="text-secondary leading-relaxed">
                      {answer.content}
                    </div>
                  </>
                ) : (
                  <>
                    <h4 className="font-semibold text-primary mb-4">
                      Explanation
                    </h4>
                    <div className="text-secondary leading-relaxed">
                      {answer as ReactNode}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThinkingExercise;
